import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { getConsent, CONSENT_EVENT, clearThirdPartyCookies } from '@/lib/consent';

/**
 * AnalyticsTracker — one POST /analytics/track per page view.
 * Without statistics consent the server only adds to an anonymous daily aggregate
 * (no cookie). With consent it uses the first-party `intimate_visitor` cookie.
 * GA4 loads only with statistics consent, Clarity only with session-recording consent,
 * and only when their IDs are configured in /admin/analytics → Integrações.
 */
const UUID_CONTENT_PATH = /^\/(story|read|series|prazer|confessions|channel)\/[0-9a-f]{8}-[0-9a-f]{4}-/i;

const injectGa4 = (id) => {
  if (window.__intimate_ga4 || !/^G-[A-Z0-9]+$/i.test(id)) return;
  window.__intimate_ga4 = true;
  const s1 = document.createElement('script');
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s1);
  const s2 = document.createElement('script');
  s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}',{anonymize_ip:true});`;
  document.head.appendChild(s2);
};

const injectClarity = (id) => {
  if (window.__intimate_clarity || !/^[a-z0-9]+$/i.test(id)) return;
  window.__intimate_clarity = true;
  const s = document.createElement('script');
  s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,'clarity','script','${id}');`;
  document.head.appendChild(s);
};

export default function AnalyticsTracker() {
  const location = useLocation();
  const { loading } = useAuth();
  const [consent, setConsent] = useState(getConsent());

  // React to consent changes; on withdrawal stop collection and remove identifiers.
  useEffect(() => {
    const onChange = (e) => {
      const { prev, next } = e.detail || {};
      setConsent(next);
      if (prev?.statistics && !next?.statistics) {
        api.post('/analytics/consent/withdraw').catch(() => {});
        clearThirdPartyCookies(['_ga', '_gid', '_gat']);
      }
      if (prev?.session_recording && !next?.session_recording) clearThirdPartyCookies(['_clck', '_clsk', 'CLID']);
      const loadedAndWithdrawn = (window.__intimate_ga4 && !next?.statistics) || (window.__intimate_clarity && !next?.session_recording);
      if (loadedAndWithdrawn) window.location.reload();
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  // Load third-party tools only for the categories the visitor accepted.
  useEffect(() => {
    if (!consent?.statistics && !consent?.session_recording) return;
    api.get('/analytics/public-settings').then(({ data }) => {
      if (consent.statistics && data?.ga4_measurement_id) injectGa4(data.ga4_measurement_id.trim());
      if (consent.session_recording && data?.clarity_project_id) injectClarity(data.clarity_project_id.trim());
    }).catch(() => {});
  }, [consent?.statistics, consent?.session_recording]);

  // Page-view ping on every location change. Fire-and-forget.
  useEffect(() => {
    const path = location.pathname + (location.search || '');
    if (path.startsWith('/admin') || path.startsWith('/embed')) return;
    // Wait for the session check so each visit is counted once.
    if (loading) return;
    // UUID content links are instantly replaced by the slug URL, which is counted instead.
    if (UUID_CONTENT_PATH.test(location.pathname)) return;
    const statistics = !!getConsent()?.statistics;
    api.post('/analytics/track', {
      path,
      referrer: document.referrer || '',
      title: statistics ? document.title || '' : '',
      consent: statistics,
    }).catch(() => {});
    if (statistics && typeof window.gtag === 'function') {
      try { window.gtag('event', 'page_view', { page_path: path, page_title: document.title }); } catch (_e) { /* gtag not loaded */ }
    }
  }, [location.pathname, location.search, loading]);

  return null;
}
